import React, { useState, useEffect, useRef } from 'react';
import Layout from '../components/Layout';
import api from '../services/api';
import { FiSend, FiMessageSquare, FiLoader, FiAlertCircle } from 'react-icons/fi';
import { formatTime } from '../utils/dateUtils';

function AIChat() {
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState(null);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    // Check AI service status
    checkStatus();
    
    // Add welcome message
    setMessages([{
      id: 'welcome',
      role: 'assistant',
      content: 'Hello! I\'m your AI assistant. I can help you query guest information, service requests, statistics, room details, and more. Ask me anything about your resort data!',
      timestamp: new Date().toISOString()
    }]);
  }, []);

  const scrollToBottom = (behavior = 'auto') => {
    messagesEndRef.current?.scrollIntoView({ behavior, block: 'end' });
  };

  useEffect(() => {
    scrollToBottom('auto');
    const timer = setTimeout(() => {
      scrollToBottom('auto');
    }, 60);
    return () => clearTimeout(timer);
  }, [messages]);

  const checkStatus = async () => {
    try {
      const response = await api.get('/api/ai-chat/status');
      setStatus(response.data);
    } catch (error) {
      console.error('Failed to check AI status:', error);
      setStatus({ configured: false, serviceType: 'None' });
    }
  };

  const handleSend = async (e) => {
    e.preventDefault();
    
    if (!inputMessage.trim() || loading) return;

    const userMessage = {
      id: Date.now().toString(),
      role: 'user',
      content: inputMessage.trim(),
      timestamp: new Date().toISOString()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputMessage('');
    setLoading(true);

    try {
      const response = await api.post('/api/ai-chat/chat', {
        message: userMessage.content
      });

      const assistantMessage = {
        id: (Date.now() + 1).toString(),
        role: 'assistant',
        content: response.data.response,
        timestamp: response.data.timestamp || new Date().toISOString()
      };

      setMessages(prev => [...prev, assistantMessage]);
    } catch (error) {
      const errorMessage = {
        id: (Date.now() + 1).toString(),
        role: 'assistant',
        content: error.response?.data?.error || 'Sorry, I encountered an error. Please try again.',
        timestamp: new Date().toISOString(),
        isError: true
      };

      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setLoading(false);
      inputRef.current?.focus();
    }
  };

  const exampleQueries = [
    'How many active guests do we have?',
    'Show me pending service requests',
    'What are the statistics for today?',
    'List all guests in room 205',
    'What is the average feedback rating?'
  ];

  const handleExampleClick = (example) => {
    setInputMessage(example);
    inputRef.current?.focus();
  };

  const userMessageCount = messages.filter((message) => message.role === 'user').length;
  const assistantMessageCount = messages.filter((message) => message.role === 'assistant' && !message.isError).length;
  const conversationStarted = messages.length > 1;

  return (
    <Layout>
      <div className="flex h-full flex-col px-1 py-2">
        <div className="px-4 sm:px-6 py-2">
          <div className="flex flex-col xl:flex-row xl:items-start xl:justify-between gap-4">
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 tracking-tight mb-1 flex items-center gap-2">
                <FiMessageSquare className="h-6 w-6 text-gray-700" />
                AI Assistant
              </h1>
              <p className="text-sm text-gray-500">Ask questions about guests, requests, rooms, and operations in natural language.</p>
            </div>
            {status && (
              <div className={`px-4 py-2 rounded-lg flex items-center gap-2 ${
                status.configured 
                  ? 'bg-green-50 text-green-800 border border-green-200' 
                  : 'bg-yellow-50 text-yellow-800 border border-yellow-200'
              }`}>
                {status.configured ? (
                  <>
                    <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                    <span className="text-sm font-medium">AI Ready ({status.serviceType})</span>
                  </>
                ) : (
                  <>
                    <FiAlertCircle className="w-4 h-4" />
                    <span className="text-sm font-medium">AI Not Configured</span>
                  </>
                )}
              </div>
            )}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-5">
            <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">User Prompts</p>
              <p className="text-2xl font-bold text-gray-900 mt-1">{userMessageCount}</p>
            </div>
            <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Assistant Replies</p>
              <p className="text-2xl font-bold text-gray-900 mt-1">{assistantMessageCount}</p>
            </div>
            <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Session</p>
              <p className="text-sm font-semibold text-gray-900 mt-2">
                {conversationStarted ? 'Conversation in progress' : 'Ready for first question'}
              </p>
            </div>
          </div>
        </div>

        {/* Chat Messages */}
        <div className="flex-1 overflow-y-auto bg-gray-50 p-4 sm:p-6">
          <div className="max-w-4xl mx-auto space-y-4">
            {messages.map((message) => (
              <div
                key={message.id}
                className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className={`max-w-3xl rounded-xl px-4 py-3 ${
                    message.role === 'user'
                      ? 'bg-gray-900 text-white shadow-sm'
                      : message.isError
                      ? 'bg-red-50 text-red-800 border border-red-200 shadow-sm'
                      : 'bg-white text-gray-900 border border-gray-200 shadow-sm'
                  }`}
                >
                  <p className="text-sm whitespace-pre-wrap">{message.content}</p>
                  <p className={`text-xs mt-1 ${
                    message.role === 'user' ? 'text-gray-300' : 'text-gray-500'
                  }`}>
                    {formatTime(message.timestamp, 'hh:mm a')}
                  </p>
                </div>
              </div>
            ))}

            {loading && (
              <div className="flex justify-start">
                <div className="bg-white border border-gray-200 rounded-lg px-4 py-3 shadow-sm">
                  <div className="flex items-center gap-2">
                    <FiLoader className="w-4 h-4 animate-spin text-gray-600" />
                    <span className="text-sm text-gray-600">Thinking...</span>
                  </div>
                </div>
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>
        </div>

        {/* Example Queries */}
        {messages.length === 1 && (
          <div className="px-6 sm:px-8 py-4 bg-white border-t border-gray-200">
            <div className="max-w-4xl mx-auto">
              <p className="text-xs font-semibold text-gray-600 mb-2">Try asking:</p>
              <div className="flex flex-wrap gap-2">
                {exampleQueries.map((example, index) => (
                  <button
                    key={index}
                    onClick={() => handleExampleClick(example)}
                    className="px-3 py-1.5 text-xs bg-gray-100 hover:bg-gray-200 border border-gray-200 text-gray-700 rounded-lg transition-colors"
                  >
                    {example}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Input Area */}
        <div className="p-4 sm:p-6 bg-white border-t border-gray-200">
          <form onSubmit={handleSend} className="max-w-4xl mx-auto">
            <div className="flex items-end gap-3">
              <div className="flex-1">
                <textarea
                  ref={inputRef}
                  value={inputMessage}
                  onChange={(e) => setInputMessage(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      handleSend(e);
                    }
                  }}
                  placeholder="Ask me anything about your resort data... (e.g., 'How many pending requests?', 'List active guests', 'What are today's statistics?')"
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-gray-300 focus:border-gray-300 resize-none bg-gray-50 focus:bg-white transition-colors"
                  rows={3}
                  disabled={loading || !status?.configured}
                />
              </div>
              <button
                type="submit"
                disabled={!inputMessage.trim() || loading || !status?.configured}
                className="px-6 py-3 bg-gray-900 text-white rounded-lg hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2 font-medium"
              >
                {loading ? (
                  <>
                    <FiLoader className="w-4 h-4 animate-spin" />
                    <span>Sending...</span>
                  </>
                ) : (
                  <>
                    <FiSend className="w-4 h-4" />
                    <span>Send</span>
                  </>
                )}
              </button>
            </div>
            {!status?.configured && (
              <p className="mt-2 text-xs text-yellow-600 flex items-center gap-1">
                <FiAlertCircle className="w-3 h-3" />
                AI service not configured. Please set OPENAI_API_KEY or GROQ_API_KEY environment variable.
              </p>
            )}
          </form>
        </div>
      </div>
    </Layout>
  );
}

export default AIChat;

